import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";
import {
  LEAD_SOURCES,
  POTENTIALS,
  STATUSES,
  STATUS_COLORS,
  POTENTIAL_COLORS,
  SOURCE_COLORS,
  formatINR,
} from "@/lib/constants";
import { Copy, Check, Filter, Search, Trash2, Pencil, CalendarRange, MessageCircle, HelpCircle, Send, Flame, Gift, FileText, IndianRupee, Briefcase, X } from "lucide-react";

const waLink = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  const normalized = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${normalized}`;
};

const needsHotUpdate = (l) => {
  if (l.potential !== "Hot") return false;
  if (l.status === "Deal Won" || l.status === "Deal Lost") return false;
  const qa = Array.isArray(l.qa) ? l.qa : [];
  let lastTs = 0;
  qa.forEach((x) => {
    if (x.asked_at) lastTs = Math.max(lastTs, new Date(x.asked_at).getTime());
    if (x.answered_at) lastTs = Math.max(lastTs, new Date(x.answered_at).getTime());
  });
  if (l.notes_updated_at) lastTs = Math.max(lastTs, new Date(l.notes_updated_at).getTime());
  const hrs = lastTs ? (Date.now() - lastTs) / 3600000 : Infinity;
  return hrs >= 24;
};

const qaTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
};

const wonKey = (l) => {
  const iso = l.won_at || l.created_at;
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthKeyLabel = (key) => {
  if (!key || key === "all") return "all time";
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const filterTrig =
  "h-9 w-auto gap-2 rounded-md border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-none focus:ring-2 focus:ring-blue-500/30";
const menuCls = "rounded-lg";
const itemCls = "rounded-md py-2";
const editInput = "w-full h-10 mt-0.5 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30";
const POT_DOT = { Hot: "bg-rose-500", High: "bg-emerald-500", Medium: "bg-amber-500", Low: "bg-slate-400" };

function CopyButton({ phone, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const done = () => {
      setCopied(true);
      toast.success("Number copied", { description: phone });
      setTimeout(() => setCopied(false), 1200);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(phone);
        return done();
      }
    } catch {
      /* fall through to legacy fallback */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = phone;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      data-testid={testid}
      onClick={copy}
      title="Copy number"
      className={`inline-flex items-center justify-center h-7 w-7 rounded-md border ${
        copied
          ? "bg-emerald-50 text-emerald-700 border-emerald-200 copied-flash"
          : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
      }`}
      style={{ transition: "background-color 160ms ease, color 160ms ease" }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

export default function LeadLibrary({ leads, team, refetchAll, wonMonth, onClearWonMonth }) {
  const { isOwner } = useAuth();
  const [fSource, setFSource] = useState("all");
  const [fPot, setFPot] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fPeriod, setFPeriod] = useState("30d");
  const [q, setQ] = useState("");
  const [hotOnly, setHotOnly] = useState(false);
  const [openQOnly, setOpenQOnly] = useState(false);
  const [fCreator, setFCreator] = useState("all");
  const [qaLead, setQaLead] = useState(null);
  const [newQuestion, setNewQuestion] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [posting, setPosting] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [fWonMonth, setFWonMonth] = useState(wonMonth || null);
  const [reps, setReps] = useState([]);
  const [workingRep, setWorkingRep] = useState("all");
  useEffect(() => {
    setFWonMonth(wonMonth || null);
  }, [wonMonth]);
  useEffect(() => {
    axios
      .get(`${API}/users`)
      .then((r) => {
        const seen = new Set();
        const unique = (r.data || []).filter((u) => {
          if (seen.has(u.email)) return false;
          seen.add(u.email);
          return true;
        });
        setReps(unique);
      })
      .catch(() => {});
  }, []);

  const monthOptions = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => {
      const d = new Date(l.created_at);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(key)) {
        map.set(key, d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }));
      }
    });
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [leads]);

  const filtered = useMemo(() => {
    const now = new Date();
    const cutoff30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return leads.filter((l) => {
      if (fWonMonth) {
        if (l.status !== "Deal Won") return false;
        return fWonMonth === "all" || wonKey(l) === fWonMonth;
      }
      if (l.direct_closing) return false; // direct closings live in Current Orders, not the library
      if (fCreator !== "all" && (l.created_by || "Unknown") !== fCreator) return false;
      if (fSource !== "all" && l.lead_source !== fSource) return false;
      if (fPot !== "all" && l.potential !== fPot) return false;
      if (fStatus !== "all" && l.status !== fStatus) return false;
      if (hotOnly && !needsHotUpdate(l)) return false;
      if (openQOnly && !(Array.isArray(l.qa) && l.qa.some((x) => !x.answer))) return false;
      if (fPeriod !== "all") {
        const d = new Date(l.created_at);
        if (fPeriod === "30d") {
          if (isNaN(d) || d < cutoff30) return false;
        } else {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          if (key !== fPeriod) return false;
        }
      }
      if (q) {
        const s = q.toLowerCase();
        if (
          !l.company_name.toLowerCase().includes(s) &&
          !l.location.toLowerCase().includes(s) &&
          !l.phone.toLowerCase().includes(s) &&
          !l.product.toLowerCase().includes(s) &&
          !(l.contact_person || "").toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [leads, fSource, fPot, fStatus, fPeriod, q, hotOnly, fCreator, openQOnly, fWonMonth]);

  const openQCount = useMemo(
    () => leads.filter((l) => !l.direct_closing && Array.isArray(l.qa) && l.qa.some((x) => !x.answer)).length,
    [leads]
  );

  const creatorCounts = useMemo(() => {
    const now = new Date();
    const cutoff30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const map = new Map();
    leads.forEach((l) => {
      if (l.direct_closing) return;
      if (fPeriod !== "all") {
        const d = new Date(l.created_at);
        if (fPeriod === "30d") {
          if (isNaN(d) || d < cutoff30) return;
        } else {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          if (key !== fPeriod) return;
        }
      }
      const name = l.created_by || "Unknown";
      map.set(name, (map.get(name) || 0) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [leads, fPeriod]);

  const hotCount = useMemo(() => leads.filter(needsHotUpdate).length, [leads]);

  const workingLeads = useMemo(
    () =>
      leads.filter(
        (l) =>
          !l.direct_closing &&
          l.working_rep &&
          l.status !== "Deal Won" &&
          l.status !== "Deal Lost"
      ),
    [leads]
  );
  const repWorkingCount = (name) => workingLeads.filter((l) => l.working_rep === name).length;
  const panelLeads = useMemo(
    () => (workingRep === "all" ? workingLeads : workingLeads.filter((l) => l.working_rep === workingRep)),
    [workingLeads, workingRep]
  );
  const chipCls = (active) =>
    `inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold ${
      active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
    }`;

  const createOrder = async (l) => {
    try {
      await axios.post(`${API}/leads/${l.id}/to-order`);
      toast.success("Order created from deal");
      await refetchAll();
    } catch {
      toast.error("Failed to create order");
    }
  };

  const patchLead = async (id, patch) => {
    try {
      await axios.patch(`${API}/leads/${id}`, patch);
      await refetchAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Lead removed");
      await refetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openEditLead = (l) => {
    setEditLead(l);
    setEditForm({
      company_name: l.company_name || "",
      contact_person: l.contact_person || "",
      phone: l.phone || "",
      location: l.location || "",
      product: l.product || "",
      sales_value: l.sales_value || 0,
      potential: l.potential || "High",
      status: l.status || "New Lead",
      assigned_rep: l.assigned_rep || "",
    });
  };

  const saveEditLead = async () => {
    if (!editForm.company_name) {
      toast.error("Company is required");
      return;
    }
    try {
      await axios.patch(`${API}/leads/${editLead.id}`, {
        company_name: editForm.company_name,
        contact_person: editForm.contact_person,
        phone: editForm.phone,
        location: editForm.location,
        product: editForm.product,
        sales_value: Number(editForm.sales_value) || 0,
        potential: editForm.potential,
        status: editForm.status,
        assigned_rep: editForm.assigned_rep || null,
      });
      toast.success("Lead updated");
      setEditLead(null);
      setEditForm(null);
      await refetchAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const openQA = (lead) => {
    setQaLead(lead);
    setNewQuestion("");
    setAnswerDrafts({});
  };

  const askQuestion = async () => {
    const text = newQuestion.trim();
    if (!text || !qaLead) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/leads/${qaLead.id}/questions`, { question: text });
      setQaLead(r.data);
      setNewQuestion("");
      await refetchAll();
    } catch {
      toast.error("Failed to post question");
    } finally {
      setPosting(false);
    }
  };

  const submitAnswer = async (qid) => {
    const text = (answerDrafts[qid] || "").trim();
    if (!text || !qaLead) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/leads/${qaLead.id}/questions/${qid}/answer`, { answer: text });
      setQaLead(r.data);
      setAnswerDrafts((d) => ({ ...d, [qid]: "" }));
      await refetchAll();
    } catch {
      toast.error("Failed to post answer");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 w-full max-w-[1600px] mx-auto">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Lead library & sales pipeline
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Filter, call, negotiate and close. Every change updates the dashboard live.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of{" "}
          <span className="font-semibold text-slate-900">{leads.length}</span> leads
        </div>
      </header>

      {hotCount > 0 && (
        <button
          data-testid="hot-leads-alert"
          onClick={() => setHotOnly((v) => !v)}
          className={`w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border text-left ${
            hotOnly
              ? "bg-rose-600 border-rose-600 text-white"
              : "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
          }`}
          style={{ transition: "background-color 160ms ease" }}
        >
          <span
            className={`flex items-center justify-center w-9 h-9 rounded-lg ${
              hotOnly ? "bg-white/20" : "bg-rose-100"
            }`}
          >
            <Flame size={18} className="flicker" />
          </span>
          <span className="flex-1">
            <span className="block font-bold text-sm">
              {hotCount} hot lead{hotCount !== 1 ? "s" : ""} need{hotCount === 1 ? "s" : ""} an update now
            </span>
            <span className="block text-xs opacity-90">
              {hotOnly
                ? "Showing only hot leads that need attention — click to show all leads"
                : "Add a note/update to stop the alert · click to review them now"}
            </span>
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">
            {hotOnly ? "Show all" : "Review now"}
          </span>
        </button>
      )}

      {fWonMonth && (
        <div data-testid="won-month-banner" className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-800">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-100 shrink-0"><IndianRupee size={18} /></span>
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm">
              Deals closed in {monthKeyLabel(fWonMonth)} · {filtered.length} · {formatINR(filtered.reduce((s, l) => s + (l.sales_value || 0), 0))}
            </span>
            <span className="block text-xs opacity-90">All won deals by close date (includes direct closings) — review for anything missing.</span>
          </span>
          <button data-testid="won-month-clear" onClick={() => { setFWonMonth(null); onClearWonMonth && onClearWonMonth(); }} className="text-xs font-bold uppercase tracking-wide hover:underline shrink-0">Clear</button>
        </div>
      )}

      {openQCount > 0 && (
        <button
          data-testid="open-questions-alert"
          onClick={() => setOpenQOnly((v) => !v)}
          className={`w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border text-left ${
            openQOnly
              ? "bg-rose-600 border-rose-600 text-white"
              : "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
          }`}
          style={{ transition: "background-color 160ms ease" }}
        >
          <span
            className={`flex items-center justify-center w-9 h-9 rounded-lg ${
              openQOnly ? "bg-white/20" : "bg-rose-100"
            }`}
          >
            <HelpCircle size={18} className="flicker" />
          </span>
          <span className="flex-1">
            <span className="block font-bold text-sm">
              {openQCount} lead{openQCount !== 1 ? "s" : ""} need{openQCount === 1 ? "s" : ""} an answer
            </span>
            <span className="block text-xs opacity-90">
              {openQOnly
                ? "Showing only leads with an open question — click to show all leads"
                : "A question is awaiting an answer · click to review them now"}
            </span>
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">
            {openQOnly ? "Show all" : "Answer now"}
          </span>
        </button>
      )}

      <div data-testid="working-leads-panel" className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 shrink-0">
              <Briefcase size={16} />
            </span>
            <div>
              <h2 className="font-heading text-base font-semibold text-slate-900 tracking-tight">
                Current working leads
              </h2>
              <p className="text-xs text-slate-500">
                Pipeline leads each rep is actively working · use the "Working on" column below to add leads
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="working-rep-all" onClick={() => setWorkingRep("all")} className={chipCls(workingRep === "all")}>
              All reps <span className="opacity-70">({workingLeads.length})</span>
            </button>
            {reps.map((r) => (
              <button
                key={r.email}
                data-testid={`working-rep-${r.email}`}
                onClick={() => setWorkingRep(r.name)}
                className={chipCls(workingRep === r.name)}
              >
                {r.name} <span className="opacity-70">({repWorkingCount(r.name)})</span>
              </button>
            ))}
          </div>
        </div>
        {panelLeads.length === 0 ? (
          <div data-testid="working-empty" className="text-sm text-slate-400 py-6 text-center">
            No leads marked as “working on”{workingRep !== "all" ? ` for ${workingRep}` : ""} yet. Set one from the
            “Working on” column in the table below.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {panelLeads.map((l, i) => {
              const st = STATUS_COLORS[l.status] || STATUS_COLORS["New Lead"];
              return (
                <div
                  key={l.id}
                  data-testid={`working-card-${i}`}
                  className="rounded-lg border border-slate-200 p-3 hover:border-blue-300"
                  style={{ transition: "border-color 160ms ease" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{l.company_name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {l.product} · Qty {l.quantity}
                      </div>
                    </div>
                    <button
                      data-testid={`working-stop-${i}`}
                      onClick={() => patchLead(l.id, { working_rep: null })}
                      title="Stop working on this lead"
                      className="text-slate-300 hover:text-rose-600 p-0.5 shrink-0"
                      style={{ transition: "color 160ms ease" }}
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ring-1 ${st.bg} ${st.text} ${st.ring}`}>
                      {l.status}
                    </span>
                    <span className="text-xs font-semibold text-slate-700">
                      {l.sales_value ? formatINR(l.sales_value) : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-[11px] text-slate-500">
                    <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold flex items-center justify-center">
                      {l.working_rep.slice(0, 1).toUpperCase()}
                    </span>
                    {l.working_rep}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500 mr-1">
          <Filter size={14} />
          <span className="uppercase tracking-[0.12em] font-semibold">Filters</span>
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="filter-search"
            placeholder="Search company, contact, phone, product…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
        <Select value={fSource} onValueChange={setFSource}>
          <SelectTrigger data-testid="filter-source" className={`${filterTrig} min-w-[150px]`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuCls}>
            <SelectItem value="all" className={itemCls}>All sources</SelectItem>
            {LEAD_SOURCES.map((s) => (
              <SelectItem key={s} value={s} className={itemCls}>
                <span className="flex items-center gap-2.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: SOURCE_COLORS[s] }} />
                  {s}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fPot} onValueChange={setFPot}>
          <SelectTrigger data-testid="filter-potential" className={`${filterTrig} min-w-[150px]`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuCls}>
            <SelectItem value="all" className={itemCls}>All potentials</SelectItem>
            {POTENTIALS.map((p) => (
              <SelectItem key={p} value={p} className={itemCls}>
                <span className="flex items-center gap-2.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${POT_DOT[p]}`} />
                  {p}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filter-status" className={`${filterTrig} min-w-[150px]`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuCls}>
            <SelectItem value="all" className={itemCls}>All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} className={itemCls}>
                <span className="flex items-center gap-2.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${(STATUS_COLORS[s] || STATUS_COLORS["New Lead"]).dot}`} />
                  {s}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fPeriod} onValueChange={setFPeriod}>
          <SelectTrigger data-testid="filter-period" className={`${filterTrig} min-w-[175px] pl-8 relative`}>
            <CalendarRange size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuCls}>
            <SelectItem value="30d" className={itemCls}>Last 30 days</SelectItem>
            <SelectItem value="all" className={itemCls}>All time (from start)</SelectItem>
            {monthOptions.length > 0 && <SelectSeparator />}
            {monthOptions.map(([key, label]) => (
              <SelectItem key={key} value={key} className={itemCls}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fCreator} onValueChange={setFCreator}>
          <SelectTrigger data-testid="filter-creator" className={`${filterTrig} min-w-[150px]`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={menuCls}>
            <SelectItem value="all" className={itemCls}>All reps</SelectItem>
            {creatorCounts.map(([name, count]) => (
              <SelectItem key={name} value={name} className={itemCls}>
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center">
                    {name.slice(0, 1).toUpperCase()}
                  </span>
                  {name} <span className="text-slate-400">({count})</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-[0.1em]">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Date</th>
                <th className="text-left font-semibold px-4 py-3">Company & contact</th>
                <th className="text-left font-semibold px-4 py-3">Source</th>
                <th className="text-left font-semibold px-4 py-3">Product & qty</th>
                <th className="text-left font-semibold px-4 py-3">Potential</th>
                <th className="text-left font-semibold px-4 py-3">Assigned rep</th>
                <th className="text-left font-semibold px-4 py-3">Working on</th>
                <th className="text-left font-semibold px-4 py-3">Sales value</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-left font-semibold px-4 py-3">Client requirement</th>
                <th className="text-left font-semibold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center text-slate-500 py-16">
                    No leads match your filters. Add one from the Lead Entry portal.
                  </td>
                </tr>
              )}
              {filtered.map((l, idx) => {
                const dt = new Date(l.created_at);
                const st = STATUS_COLORS[l.status] || STATUS_COLORS["New Lead"];
                return (
                  <tr key={l.id} data-testid={`lead-row-${idx}`} className="row-hover">
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                      <div className="font-medium text-slate-700">
                        {dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                      </div>
                      {l.created_by && (
                        <div data-testid={`lead-by-${idx}`} className="text-[10px] text-slate-400 mt-1 italic">
                          Lead by {l.created_by}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{l.company_name}</span>
                        {l.is_corporate_gifting && (
                          <span
                            data-testid={`gifting-badge-${idx}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
                            title="Corporate gifting · priority client"
                          >
                            <Gift size={10} /> Gifting
                          </span>
                        )}
                      </div>
                      {l.contact_person && (
                        <div className="text-xs text-slate-500 mt-0.5">{l.contact_person}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-xs text-slate-700">{l.phone}</span>
                        <CopyButton phone={l.phone} testid={`copy-phone-${idx}`} />
                        <a
                          data-testid={`whatsapp-${idx}`}
                          href={waLink(l.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Message on WhatsApp"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md border bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100"
                          style={{ transition: "background-color 160ms ease" }}
                        >
                          <MessageCircle size={14} />
                        </a>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{l.location}</div>
                      {l.proforma_media_id && (
                        <a
                          href={`${API}/proforma/${l.proforma_media_id}/file`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`proforma-link-${idx}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 mt-1"
                        >
                          <FileText size={11} /> View proforma
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
                        {l.lead_source}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 text-xs font-semibold">{l.product}</div>
                      <div className="text-xs text-slate-500">Qty · {l.quantity}</div>
                      {Array.isArray(l.items) && l.items.length > 1 && (
                        <div
                          className="text-[10px] text-blue-600 font-semibold mt-0.5"
                          title={l.items.map((it) => `${it.product} × ${it.quantity}`).join(", ")}
                        >
                          +{l.items.length - 1} more product{l.items.length - 1 !== 1 ? "s" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const flick = needsHotUpdate(l);
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ring-1 font-semibold ${
                              POTENTIAL_COLORS[l.potential] || POTENTIAL_COLORS.Low
                            }`}
                          >
                            {l.potential === "Hot" && (
                              <Flame
                                size={12}
                                data-testid={`hot-flicker-${idx}`}
                                className={flick ? "flicker" : ""}
                              />
                            )}
                            {l.potential}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={l.assigned_rep || "__unassigned__"}
                        onValueChange={(v) => patchLead(l.id, { assigned_rep: v === "__unassigned__" ? null : v })}
                      >
                        <SelectTrigger
                          data-testid={`assign-rep-${idx}`}
                          className="h-8 min-w-[130px] rounded-md border-slate-200 bg-white text-xs shadow-none focus:ring-2 focus:ring-blue-500/30"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={menuCls}>
                          <SelectItem value="__unassigned__" className={itemCls}>Unassigned</SelectItem>
                          {team.map((t) => (
                            <SelectItem key={t.id} value={t.name} className={itemCls}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={l.working_rep || "__none__"}
                        onValueChange={(v) => patchLead(l.id, { working_rep: v === "__none__" ? null : v })}
                      >
                        <SelectTrigger
                          data-testid={`working-select-${idx}`}
                          className={`h-8 min-w-[130px] rounded-md text-xs shadow-none focus:ring-2 focus:ring-blue-500/30 ${
                            l.working_rep
                              ? "border-blue-300 bg-blue-50 text-blue-800 font-semibold"
                              : "border-slate-200 bg-white text-slate-500"
                          }`}
                        >
                          <SelectValue placeholder="Not working" />
                        </SelectTrigger>
                        <SelectContent className={menuCls}>
                          <SelectItem value="__none__" className={itemCls}>Not working</SelectItem>
                          {reps.map((r) => (
                            <SelectItem key={r.email} value={r.name} className={itemCls}>
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-500">₹</span>
                        <input
                          key={l.sales_value}
                          data-testid={`sales-value-${idx}`}
                          type="number"
                          min="0"
                          defaultValue={l.sales_value || 0}
                          onBlur={(e) => {
                            const v = Number(e.target.value) || 0;
                            if (v !== (l.sales_value || 0)) patchLead(l.id, { sales_value: v });
                          }}
                          className="w-24 h-8 px-2 rounded-md border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        />
                      </div>
                      {l.status === "Deal Won" && l.sales_value > 0 && (
                        <div className="text-[10px] text-emerald-700 font-semibold mt-1">
                          {formatINR(l.sales_value)} won
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => patchLead(l.id, { status: v })}>
                        <SelectTrigger
                          data-testid={`status-select-${idx}`}
                          className={`h-8 min-w-[140px] rounded-md text-xs font-semibold ring-1 border-0 shadow-none focus:ring-2 focus:ring-blue-500/30 ${st.bg} ${st.text} ${st.ring}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={menuCls}>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className={itemCls}>
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${(STATUS_COLORS[s] || STATUS_COLORS["New Lead"]).dot}`} />
                                {s}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {l.status === "Deal Won" &&
                        (l.order_created ? (
                          <div className="text-[10px] text-emerald-700 font-semibold mt-1">Order created ✓</div>
                        ) : (
                          <button
                            data-testid={`to-order-${idx}`}
                            onClick={() => createOrder(l)}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700"
                          >
                            + Create order
                          </button>
                        ))}
                    </td>
                    <td className="px-4 py-3 max-w-[240px]">
                      {(() => {
                        const qa = Array.isArray(l.qa) ? l.qa : [];
                        const openQ = qa.some((x) => !x.answer);
                        const answered = qa.filter((x) => x.answer).length;
                        if (qa.length === 0) {
                          return (
                            <button
                              data-testid={`qa-btn-${idx}`}
                              onClick={() => openQA(l)}
                              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-semibold border bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                              style={{ transition: "background-color 160ms ease" }}
                            >
                              <HelpCircle size={13} /> Ask a question
                            </button>
                          );
                        }
                        return (
                          <button
                            data-testid={`qa-btn-${idx}`}
                            onClick={() => openQA(l)}
                            title="Open Q&A thread"
                            className="text-left"
                          >
                            {openQ ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600">
                                <HelpCircle size={13} data-testid={`qa-flicker-${idx}`} className="flicker shrink-0" />
                                Awaiting answer
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                                <MessageCircle size={13} className="shrink-0" />
                                {answered} answered
                              </span>
                            )}
                            <span className="block text-[11px] text-slate-400 mt-0.5 line-clamp-1">
                              {qa[qa.length - 1].question}
                            </span>
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`edit-lead-${idx}`}
                          onClick={() => openEditLead(l)}
                          title="Edit lead"
                          className="text-slate-400 hover:text-blue-600 p-1"
                          style={{ transition: "color 160ms ease" }}
                        >
                          <Pencil size={15} />
                        </button>
                        {isOwner && (
                          <button
                            data-testid={`delete-lead-${idx}`}
                            onClick={() => removeLead(l.id)}
                            className="text-slate-400 hover:text-rose-600 p-1"
                            style={{ transition: "color 160ms ease" }}
                            aria-label="Delete lead"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!qaLead} onOpenChange={(o) => !o && setQaLead(null)}>
        <DialogContent data-testid="qa-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Q&amp;A {qaLead ? `· ${qaLead.company_name}` : ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Ask the team a question on this lead — anyone can answer. Open questions flicker until answered.
          </p>
          <div className="max-h-[340px] overflow-y-auto space-y-3 pr-1">
            {(!qaLead || !(qaLead.qa || []).length) && (
              <div className="text-sm text-slate-400 py-6 text-center">No questions yet. Ask the first one below.</div>
            )}
            {qaLead && (qaLead.qa || []).map((item) => (
              <div key={item.id} data-testid={`qa-item-${item.id}`} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-start gap-2">
                  <HelpCircle size={15} className="text-blue-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900">{item.question}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {item.asked_by} · {qaTime(item.asked_at)}
                    </div>
                  </div>
                </div>
                {item.answer ? (
                  <div className="flex items-start gap-2 mt-2 ml-1 pl-2 border-l-2 border-emerald-200">
                    <MessageCircle size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-700">{item.answer}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {item.answered_by} · {qaTime(item.answered_at)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      data-testid={`qa-answer-input-${item.id}`}
                      value={answerDrafts[item.id] || ""}
                      onChange={(e) => setAnswerDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && submitAnswer(item.id)}
                      placeholder="Type an answer…"
                      className="flex-1 h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                    <button
                      data-testid={`qa-answer-btn-${item.id}`}
                      onClick={() => submitAnswer(item.id)}
                      disabled={posting}
                      className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold"
                    >
                      Answer
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              data-testid="qa-question-input"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askQuestion()}
              placeholder="Ask a question to the team…"
              className="flex-1 h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            <button
              data-testid="qa-ask-btn"
              onClick={askQuestion}
              disabled={posting}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold"
            >
              <Send size={15} /> Ask
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLead} onOpenChange={(o) => { if (!o) { setEditLead(null); setEditForm(null); } }}>
        <DialogContent data-testid="edit-lead-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit lead {editLead ? `· ${editLead.company_name}` : ""}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Company</label>
                <input data-testid="edit-lead-company" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} className={editInput} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Contact person</label>
                <input data-testid="edit-lead-contact" value={editForm.contact_person} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} className={editInput} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Phone</label>
                <input data-testid="edit-lead-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={editInput} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Location</label>
                <input data-testid="edit-lead-location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className={editInput} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Product</label>
                <input data-testid="edit-lead-product" value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })} className={editInput} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Sale value ₹</label>
                <input data-testid="edit-lead-value" type="number" min="0" value={editForm.sales_value} onChange={(e) => setEditForm({ ...editForm, sales_value: e.target.value })} className={editInput} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Assigned rep</label>
                <Select value={editForm.assigned_rep || "__unassigned__"} onValueChange={(v) => setEditForm({ ...editForm, assigned_rep: v === "__unassigned__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-lead-rep" className="h-10 mt-0.5 rounded-md border-slate-200 text-sm shadow-none"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className={menuCls}>
                    <SelectItem value="__unassigned__" className={itemCls}>Unassigned</SelectItem>
                    {team.map((t) => (<SelectItem key={t.id} value={t.name} className={itemCls}>{t.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Potential</label>
                <Select value={editForm.potential} onValueChange={(v) => setEditForm({ ...editForm, potential: v })}>
                  <SelectTrigger data-testid="edit-lead-potential" className="h-10 mt-0.5 rounded-md border-slate-200 text-sm shadow-none"><SelectValue /></SelectTrigger>
                  <SelectContent className={menuCls}>
                    {POTENTIALS.map((p) => (<SelectItem key={p} value={p} className={itemCls}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Status</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-lead-status" className="h-10 mt-0.5 rounded-md border-slate-200 text-sm shadow-none"><SelectValue /></SelectTrigger>
                  <SelectContent className={menuCls}>
                    {STATUSES.map((s) => (<SelectItem key={s} value={s} className={itemCls}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setEditLead(null); setEditForm(null); }} className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button data-testid="edit-lead-save" onClick={saveEditLead} className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Save changes</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
